import { ReactNode, useMemo, useState } from 'react';
import { Button, Tabs, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    TbArrowBack,
    TbCash,
    TbCircleCheck,
    TbCircleX,
    TbClock,
    TbFileInvoice,
    TbListDetails,
    TbMail,
    TbRefresh,
    TbSend,
} from 'react-icons/tb';

import type { FeatureAddOnKey, PlanKey } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReferralActivity } from '@/hooks/useReferralSummary';
import type {
    ReferralInvitation,
    ReferralInvoiceableItem,
    ReferralPayout,
    ReferralTransaction,
} from '@/services/database';
import {
    AnalyticsSectionCard,
    EmptyView,
    FeatureAddOnPopover,
    MiddleDot,
    PlanPopover,
    PlatformAlert,
    SmallTag,
} from '@/shared';
import PlatformTable from '@/shared/components/tables/PlatformTable';
import { formatStripeMoney } from '@/utilities';

const ACTIVITY_HORIZONTAL_INSET = '0.625rem';
const ACTIVITY_PAGE_SIZE = 10;
const ACTIVITY_COLUMN_WIDTHS = {
    date: 240,
    primary: 440,
    status: 180,
    value: 180,
} as const;
const ACTIVITY_TABLE_MIN_WIDTH =
    Object.values(ACTIVITY_COLUMN_WIDTHS).reduce((total, width) => total + width, 0) + ACTIVITY_COLUMN_WIDTHS.value;

const dateFormatter = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
});

const formatDate = (value: string | null) => (value ? dateFormatter.format(new Date(value)) : '—');
const formatStatus = (value: string) => value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const activityCollator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });
const compareText = (left: string | null | undefined, right: string | null | undefined) =>
    activityCollator.compare(left?.trim() ?? '', right?.trim() ?? '');
const activityTimestamp = (value: string | null | undefined) => {
    const timestamp = value ? Date.parse(value) : Number.NaN;

    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};
const compareDate = (left: string | null | undefined, right: string | null | undefined) =>
    activityTimestamp(left) - activityTimestamp(right);
const planKeys = new Set<PlanKey>(['enterprise', 'plus', 'premium', 'pro']);
const featureAddOnKeys = new Set<FeatureAddOnKey>(['lifecycles', 'relations']);

const transactionSortLabel = (transaction: ReferralTransaction) => {
    const items = transaction.invoiceableItems ?? [];

    return items.length
        ? items.map((item) => item.label || formatStatus(item.key)).join(' · ')
        : transaction.description || `${formatStatus(transaction.type)} Commission`;
};

const ActivityStatusTag = ({ status }: { status: string }) => {
    const { token } = useAntDesignTheme();
    const normalizedStatus = status.replaceAll('_', ' ').toLowerCase();
    const presentation = (() => {
        if (['completed', 'converted', 'processed', 'registered', 'tracked'].includes(normalizedStatus)) {
            return { color: token.colorSuccess, icon: <TbCircleCheck /> };
        }
        if (normalizedStatus === 'sent') {
            return { color: token.colorInfo, icon: <TbSend /> };
        }
        if (['pending', 'processing'].includes(normalizedStatus)) {
            return { color: token.colorWarning, icon: <TbClock /> };
        }
        if (['partially disputed', 'partially refunded', 'refunded'].includes(normalizedStatus)) {
            return { color: token.colorWarning, icon: <TbArrowBack /> };
        }
        if (['canceled', 'disputed', 'failed'].includes(normalizedStatus)) {
            return { color: token.colorError, icon: <TbCircleX /> };
        }

        return { color: token.colorTextSecondary, icon: <TbClock /> };
    })();

    return (
        <SmallTag
            accentColor={presentation.color}
            compact
            icon={presentation.icon}
            message={formatStatus(status)}
            styles={{ gap: '0.25rem', marginLeft: 0, minWidth: 'max-content', whiteSpace: 'nowrap' }}
        />
    );
};

const InvoiceableProduct = ({ item }: { item: ReferralInvoiceableItem }) => {
    const product = (() => {
        if (item.type === 'plan' && planKeys.has(item.key as PlanKey)) {
            return <PlanPopover defaultInterval={item.interval ?? undefined} plan={item.key as PlanKey} />;
        }
        if (item.type === 'featureAddOn' && featureAddOnKeys.has(item.key as FeatureAddOnKey)) {
            return (
                <FeatureAddOnPopover
                    defaultInterval={item.interval ?? undefined}
                    itemKey={item.key as FeatureAddOnKey}
                />
            );
        }

        return <Typography.Text>{item.label || formatStatus(item.key)}</Typography.Text>;
    })();

    return (
        <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
            {product}
            {item.quantity > 1 ? <Typography.Text type='secondary'>× {item.quantity}</Typography.Text> : null}
        </span>
    );
};

const InvoiceableProducts = ({ transaction }: { transaction: ReferralTransaction }) => {
    const items = transaction.invoiceableItems ?? [];

    if (!items.length) {
        return (
            <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                {transaction.description || `${formatStatus(transaction.type)} Commission`}
            </Typography.Text>
        );
    }

    return (
        <span style={{ alignItems: 'center', display: 'inline-flex', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            {items.map((item, index) => (
                <span
                    key={`${item.type}-${item.key}-${index}`}
                    style={{ alignItems: 'center', display: 'inline-flex' }}
                >
                    {index ? <MiddleDot horizontalMargin='0.375rem' /> : null}
                    <InvoiceableProduct item={item} />
                </span>
            ))}
        </span>
    );
};

const transactionColumns: ColumnsType<ReferralTransaction> = [
    {
        dataIndex: 'description',
        ellipsis: true,
        key: 'transaction',
        render: (_: ReferralTransaction['description'], transaction) => (
            <InvoiceableProducts transaction={transaction} />
        ),
        sorter: (left, right) => compareText(transactionSortLabel(left), transactionSortLabel(right)),
        title: 'Transaction',
        width: ACTIVITY_COLUMN_WIDTHS.primary,
    },
    {
        align: 'right',
        dataIndex: 'status',
        key: 'status',
        render: (status: ReferralTransaction['status']) => <ActivityStatusTag status={status} />,
        sorter: (left, right) => compareText(formatStatus(left.status), formatStatus(right.status)),
        title: 'Status',
        width: ACTIVITY_COLUMN_WIDTHS.status,
    },
    {
        align: 'right',
        key: 'amount',
        render: (_, transaction) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {formatStripeMoney(transaction.amount, transaction.currency)}
            </Typography.Text>
        ),
        sorter: (left, right) => left.amount - right.amount,
        title: 'Sale Amount',
        width: ACTIVITY_COLUMN_WIDTHS.value,
    },
    {
        align: 'right',
        key: 'earnings',
        render: (_, transaction) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {transaction.earnings === null ? '—' : formatStripeMoney(transaction.earnings, transaction.currency)}
            </Typography.Text>
        ),
        sorter: (left, right) => (left.earnings ?? 0) - (right.earnings ?? 0),
        title: 'Gross Commission',
        width: ACTIVITY_COLUMN_WIDTHS.value,
    },
    {
        align: 'right',
        dataIndex: 'createdAt',
        key: 'date',
        render: (createdAt: ReferralTransaction['createdAt']) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {formatDate(createdAt)}
            </Typography.Text>
        ),
        sorter: (left, right) => compareDate(left.createdAt, right.createdAt),
        title: 'Date',
        width: ACTIVITY_COLUMN_WIDTHS.date,
    },
];

const payoutColumns: ColumnsType<ReferralPayout> = [
    {
        ellipsis: true,
        key: 'period',
        render: (_, payout) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                {payout.periodStart
                    ? `${formatDate(payout.periodStart)} – ${formatDate(payout.periodEnd)}`
                    : payout.description || 'Referral Payout'}
            </Typography.Text>
        ),
        sorter: (left, right) => compareDate(left.periodStart ?? left.createdAt, right.periodStart ?? right.createdAt),
        title: 'Payout Period',
        width: ACTIVITY_COLUMN_WIDTHS.primary,
    },
    {
        align: 'right',
        dataIndex: 'status',
        key: 'status',
        render: (status: ReferralPayout['status']) => <ActivityStatusTag status={status} />,
        sorter: (left, right) => compareText(formatStatus(left.status), formatStatus(right.status)),
        title: 'Status',
        width: ACTIVITY_COLUMN_WIDTHS.status,
    },
    {
        align: 'right',
        key: 'amount',
        render: (_, payout) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {formatStripeMoney(payout.amount, payout.currency)}
            </Typography.Text>
        ),
        sorter: (left, right) => left.amount - right.amount,
        title: 'Amount',
        width: ACTIVITY_COLUMN_WIDTHS.value,
    },
    {
        align: 'right',
        key: 'paid',
        render: (_, payout) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {formatDate(payout.paidAt ?? payout.initiatedAt)}
            </Typography.Text>
        ),
        sorter: (left, right) => compareDate(left.paidAt ?? left.initiatedAt, right.paidAt ?? right.initiatedAt),
        title: 'Paid',
        width: ACTIVITY_COLUMN_WIDTHS.date,
    },
];

const invitationColumns: ColumnsType<ReferralInvitation> = [
    {
        dataIndex: 'emailAddress',
        ellipsis: true,
        key: 'recipient',
        sorter: (left, right) => compareText(left.emailAddress, right.emailAddress),
        title: 'Recipient',
        width: ACTIVITY_COLUMN_WIDTHS.primary,
    },
    {
        align: 'right',
        dataIndex: 'status',
        key: 'status',
        render: (status: ReferralInvitation['status']) => <ActivityStatusTag status={status} />,
        sorter: (left, right) => compareText(formatStatus(left.status), formatStatus(right.status)),
        title: 'Status',
        width: ACTIVITY_COLUMN_WIDTHS.status,
    },
    {
        align: 'right',
        dataIndex: 'sentAt',
        key: 'sent',
        render: (sentAt: ReferralInvitation['sentAt']) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {formatDate(sentAt)}
            </Typography.Text>
        ),
        sorter: (left, right) => compareDate(left.sentAt, right.sentAt),
        title: 'Sent',
        width: ACTIVITY_COLUMN_WIDTHS.value,
    },
    {
        align: 'right',
        dataIndex: 'registeredAt',
        key: 'registered',
        render: (registeredAt: ReferralInvitation['registeredAt']) => (
            <Typography.Text style={{ whiteSpace: 'nowrap' }} type='secondary'>
                {formatDate(registeredAt)}
            </Typography.Text>
        ),
        sorter: (left, right) => compareDate(left.registeredAt, right.registeredAt),
        title: 'Registered',
        width: ACTIVITY_COLUMN_WIDTHS.date,
    },
];

interface ActivityListProps<TItem extends { id: string }> {
    columns: ColumnsType<TItem>;
    emptyDescription: string;
    emptyLabel: string;
    emptyIcon: ReactNode;
    error: Error | null;
    fetchingNextPage: boolean;
    hasNextPage: boolean;
    items: TItem[];
    loading: boolean;
    onLoadMore: () => void;
    onRetry: () => void;
}

const ActivityList = <TItem extends { id: string }>({
    columns,
    emptyDescription,
    emptyIcon,
    emptyLabel,
    error,
    fetchingNextPage,
    hasNextPage,
    items,
    loading,
    onLoadMore,
    onRetry,
}: ActivityListProps<TItem>) => {
    if (error) {
        return (
            <PlatformAlert
                action={
                    <Button icon={<TbRefresh />} onClick={onRetry} size='small'>
                        Try again
                    </Button>
                }
                description='The latest referral activity could not be loaded. Your referral link and invitations remain available.'
                message='Unable to load referral activity'
                showIcon
                type='error'
            />
        );
    }

    return (
        <div style={{ minHeight: '20rem', minWidth: 0, overflow: 'hidden' }}>
            <PlatformTable<TItem>
                columns={columns}
                dataSource={items}
                footer={
                    hasNextPage
                        ? () => (
                              <div style={{ textAlign: 'center' }}>
                                  <Button loading={fetchingNextPage} onClick={onLoadMore} size='small'>
                                      Load More
                                  </Button>
                              </div>
                          )
                        : undefined
                }
                loading={loading}
                locale={{
                    emptyText: (
                        <EmptyView description={emptyDescription} height='15rem' icon={emptyIcon} label={emptyLabel} />
                    ),
                }}
                pagination={{
                    defaultPageSize: ACTIVITY_PAGE_SIZE,
                    hideOnSinglePage: false,
                    pageSizeOptions: [10, 25, 50],
                    position: ['bottomRight'],
                    showSizeChanger: items.length > ACTIVITY_PAGE_SIZE,
                    size: 'small',
                }}
                rowKey='id'
                onRow={() => ({ style: { whiteSpace: 'nowrap' } })}
                scroll={{ x: ACTIVITY_TABLE_MIN_WIDTH, y: '16rem' }}
                size='small'
                sticky
            />
        </div>
    );
};

const getCountLabel = (total: number, hasMore: boolean, loaded: number) => (hasMore ? `${loaded}+` : total);

const ReferralActivityDashboard = () => {
    const [activeKey, setActiveKey] = useState('transactions');
    const transactionsQuery = useReferralActivity('transactions');
    const payoutsQuery = useReferralActivity('payouts');
    const invitationsQuery = useReferralActivity('invitations');
    const transactions = useMemo(
        () => transactionsQuery.data?.pages.flatMap(({ items }) => items) ?? [],
        [transactionsQuery.data?.pages],
    );
    const payouts = useMemo(
        () => payoutsQuery.data?.pages.flatMap(({ items }) => items) ?? [],
        [payoutsQuery.data?.pages],
    );
    const invitations = useMemo(
        () => invitationsQuery.data?.pages.flatMap(({ items }) => items) ?? [],
        [invitationsQuery.data?.pages],
    );
    const countTag = (total: number, hasMore: boolean, loaded: number, loading: boolean, unavailable = false) => {
        if (loading || unavailable || total <= 0) {
            return null;
        }

        return <SmallTag compact message={getCountLabel(total, hasMore, loaded)} styles={{ marginLeft: 0 }} />;
    };
    const tabLabel = (icon: ReactNode, label: string, tag: ReactNode) => (
        <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem', lineHeight: 1 }}>
            <span aria-hidden='true' style={{ alignItems: 'center', display: 'inline-flex', lineHeight: 0 }}>
                {icon}
            </span>
            <span>{label}</span>
            {tag}
        </span>
    );
    const transactionsTotal = transactionsQuery.data?.pages[0]?.total ?? 0;
    const payoutsTotal = payoutsQuery.data?.pages[0]?.total ?? 0;
    const payoutsUnavailable = payoutsQuery.data?.pages[0]?.unavailable ?? false;
    const payoutsDisplayUnavailable = payoutsUnavailable && payouts.length === 0;
    const invitationsTotal = invitationsQuery.data?.pages[0]?.total ?? 0;

    return (
        <AnalyticsSectionCard
            ariaLabel='Referral activity'
            contentStyle={{ minWidth: 0, paddingBottom: '0.75rem' }}
            description='Review commissions, payout progress, and every invitation sent from Countercyclical.'
            icon={<TbListDetails />}
            title='Referral Activity'
        >
            <Tabs
                activeKey={activeKey}
                items={[
                    {
                        children: (
                            <ActivityList
                                columns={transactionColumns}
                                emptyDescription='Commission activity appears after a referred customer pays an eligible monthly or annual subscription invoice.'
                                emptyIcon={<TbFileInvoice />}
                                emptyLabel='No Transactions Yet'
                                error={transactionsQuery.error}
                                fetchingNextPage={transactionsQuery.isFetchingNextPage}
                                hasNextPage={transactionsQuery.hasNextPage}
                                items={transactions}
                                loading={transactionsQuery.isLoading}
                                onLoadMore={() => void transactionsQuery.fetchNextPage()}
                                onRetry={() => void transactionsQuery.refetch()}
                            />
                        ),
                        key: 'transactions',
                        label: tabLabel(
                            <TbFileInvoice />,
                            'Transactions',
                            countTag(
                                transactionsTotal,
                                transactionsQuery.hasNextPage,
                                transactions.length,
                                transactionsQuery.isLoading,
                            ),
                        ),
                    },
                    {
                        children: (
                            <ActivityList
                                columns={payoutColumns}
                                emptyDescription={
                                    payoutsDisplayUnavailable
                                        ? 'Payout history is temporarily unavailable from the referral provider. Your tracked referral activity is unaffected.'
                                        : 'Payouts appear here after eligible commissions are included in a referral payout.'
                                }
                                emptyIcon={<TbCash />}
                                emptyLabel={payoutsDisplayUnavailable ? 'Payout History Unavailable' : 'No Payouts Yet'}
                                error={payoutsQuery.error}
                                fetchingNextPage={payoutsQuery.isFetchingNextPage}
                                hasNextPage={payoutsQuery.hasNextPage}
                                items={payouts}
                                loading={payoutsQuery.isLoading}
                                onLoadMore={() => void payoutsQuery.fetchNextPage()}
                                onRetry={() => void payoutsQuery.refetch()}
                            />
                        ),
                        key: 'payouts',
                        label: tabLabel(
                            <TbCash />,
                            'Payouts',
                            countTag(
                                payoutsTotal,
                                payoutsQuery.hasNextPage,
                                payouts.length,
                                payoutsQuery.isLoading,
                                payoutsDisplayUnavailable,
                            ),
                        ),
                    },
                    {
                        children: (
                            <ActivityList
                                columns={invitationColumns}
                                emptyDescription='Invitations sent from the referral form will appear here.'
                                emptyIcon={<TbMail />}
                                emptyLabel='No Invitations Yet'
                                error={invitationsQuery.error}
                                fetchingNextPage={invitationsQuery.isFetchingNextPage}
                                hasNextPage={invitationsQuery.hasNextPage}
                                items={invitations}
                                loading={invitationsQuery.isLoading}
                                onLoadMore={() => void invitationsQuery.fetchNextPage()}
                                onRetry={() => void invitationsQuery.refetch()}
                            />
                        ),
                        key: 'invitations',
                        label: tabLabel(
                            <TbMail />,
                            'Invitations',
                            countTag(
                                invitationsTotal,
                                invitationsQuery.hasNextPage,
                                invitations.length,
                                invitationsQuery.isLoading,
                            ),
                        ),
                    },
                ]}
                onChange={setActiveKey}
                size='small'
                tabBarStyle={{ marginBottom: 0, padding: `0 ${ACTIVITY_HORIZONTAL_INSET}` }}
            />
        </AnalyticsSectionCard>
    );
};

export default ReferralActivityDashboard;
